import { logger } from '@gem/modules/logger';

import { RLottieHandler } from './RLottieHandler';

class RLottieRunner {
  private rafId: number;
  private isRunning: boolean;
  private handlers: RLottieHandler[];
  private rerenderHandlers: Record<string, () => void>;

  constructor() {
    this.handlers = [];
    this.rerenderHandlers = {};
    this.isRunning = false;

    this.loop = this.loop.bind(this);
    this.addHandler = this.addHandler.bind(this);
    this.removeHandler = this.removeHandler.bind(this);

    document.addEventListener('visibilitychange', () => this.visibilityChangeHandler());
  }

  public addHandler(handler: RLottieHandler): void {
    if (!this.handlers.includes(handler)) {
      this.handlers.push(handler);
    }

    if (!this.isRunning) {
      this.isRunning = true;
      this.loop();
    }
  }

  public addRerenderHandler(id: string, handler: () => void): void {
    this.rerenderHandlers[id] = handler;
  }

  public removeRerenderHandler(id: string): void {
    delete this.rerenderHandlers[id];
  }

  public removeHandler(handler: RLottieHandler): void {
    const handlerIndex = this.handlers.findIndex((h) => h === handler);

    if (handlerIndex !== -1) {
      this.handlers.splice(handlerIndex, 1);
    }

    if (this.isRunning && this.handlers.length === 0) {
      this.stop();
    }
  }

  public stop(): void {
    this.isRunning = false;
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
    }
  }

  private loop(): void {
    if (this.isRunning) {
      // Обрабатываем все handlers в одном frame
      // Это гарантирует, что все canvas операции синхронизированы с VSync
      this.handlers.forEach((handler) => {
        try {
          handler.onLoop();
        } catch (error) {
          logger.get('rLottie').error('RLottieRunner.loop error: ', error);
        }
      });

      // requestAnimationFrame после всех операций в frame
      // Это гарантирует Skia Graphite правильный порядок GPU команд
      this.rafId = window.requestAnimationFrame(this.loop);
    }
  }

  private visibilityChangeHandler(): void {
    if (document.hidden) {
      // Останавливаем все рендеры при скрытии
      // На macOS это освобождает GPU ресурсы
      this.stop();
      return;
    }

    // При возвращении фокуса: полностью пересчитываем state всех анимаций
    this.handlers.forEach((handler) => {
      try {
        if (!handler.playing) {
          handler.reset();
        }
      } catch (error) {
        logger.get('rLottie').error('RLottieRunner.visibilityChangeHandler error:', error);
      }
    });

    Object.values(this.rerenderHandlers).forEach((handler) => {
      try {
        handler();
      } catch (error) {
        logger
          .get('rLottie')
          .error('RLottieRunner.visibilityChangeHandler renderer handler error:', error);
      }
    });
  }
}

export const rLottieRunner = new RLottieRunner();
